import * as React from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { connect, ResolveThunks } from "react-redux";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import TextField from "@insite/mobius/TextField";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography from "@insite/mobius/Typography";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import TextArea from "@insite/mobius/TextArea";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import submitProductRequestFormHandler from "@tCentro/Handlers/NISProductRequestFormHandler";

const mapDispatchToProps = {
    submitProductRequestFormHandler,
};

type Props = ResolveThunks<typeof mapDispatchToProps>;

export interface NISNoSearchOptimizelyFormStyles {
    firstLastNameStyle?: InjectableCss;
    centeringWrapper?: InjectableCss;
    fieldset: InjectableCss;
    submitButton: ButtonPresentationProps;
}

export const nisNoSearchOptimizelyFormStyles: NISNoSearchOptimizelyFormStyles = {
    fieldset: {
        css: css`
            border: none !important;
            padding-right: 0px !important;
            margin-right: 0px !important;
            label {
                font-size: 14px;
                font-weight: 600;
                line-height: 20px;
                color: rgb(38, 37, 37);
                margin-left: 24px;
                min-height: 22px !important;
                padding-bottom: 10px !important;
            }
            input {
                border-radius: 40px !important;
                padding: 12px 45px 12px 50px !important;
                width: 100%;
                box-sizing: border-box;
                font-family: inherit;
                font-size: 15px;
                outline: 0px;
                appearance: none;
                color: #636161;
                background-color: #f2f2f2 !important;
                border: 1px solid rgb(226, 227, 230);
                height: 48px;
            }
            input:focus {
                border: 2px solid #262525 !important;
            }
            div[class^="FormInputWrapper"] {
                margin-top: -4px !important;
            }
            input[aria-invalid="true"] {
                border: 2px solid #ed0000 !important;
            }

            [data-test-selector*="error"] {
                color: #ed0000 !important;
                padding-left: 24px !important;
                margin-bottom: 5px !important;
                font-weight: 700 !important;
                font-family: Poppins;
                font-size: 14px;
                line-height: 20px;
            }
            textarea {
                resize: vertical;
                background: rgb(242, 242, 242) !important;
                border-radius: 40px !important;
                padding: 24px 24px !important;
                min-height: 144px !important;
                width: 100%;
                box-sizing: border-box;
                font-size: 15px;
                outline: 0px;
                appearance: none;
                border: 1px solid rgb(226, 227, 230);
            }
            textarea:focus {
                border: 2px solid #262525 !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px !important;
                        margin-left: 0px !important;
                    `,
                    css`
                        padding-left: 0px !important;
                        margin-left: 0px !important;
                    `,
                    css`
                        padding-left: 0px !important;
                        margin-left: 0px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    firstLastNameStyle: {
        css: css`
            display: flex;
            gap: 20px;
            width:100%:
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            height: 150px;
        `,
    },
    submitButton: {
        css: css`
            margin-top: 5px;
            border-radius: 24px;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            float: right;
            margin-top: 4px;
            cursor: pointer;
            min-height: 40px;
            line-height: 1;
            backface-visibility: hidden;
            transition: all 200ms ease-in-out 0s;
            word-break: keep-all;
            padding: 0px 21px;
        `,
    },
};

const styles = nisNoSearchOptimizelyFormStyles;

const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

const NISNoSearchOptimizelyForm = ({ submitProductRequestFormHandler }: Props) => {
    const [firstName, setFirstName] = React.useState("");
    const [lastName, setLastName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [brandName, setBrandName] = React.useState("");
    const [productNumber, setProductNumber] = React.useState("");
    const [additionalInfo, setAdditionalInfo] = React.useState("");

    const [firstNameError, setFirstNameError] = React.useState("");
    const [lastNameError, setLastNameError] = React.useState("");
    const [emailError, setEmailError] = React.useState("");
    const [brandNameError, setBrandNameError] = React.useState("");

    const [isLoading, setIsLoading] = React.useState(false);
    const [onSuccess, setOnSuccess] = React.useState(true);

    const handleFirstNameChange = (val: string) => {
        setFirstName(val);
        setFirstNameError(val.trim() ? "" : "First name is required");
    };

    const handleLastNameChange = (val: string) => {
        setLastName(val);
        setLastNameError(val.trim() ? "" : "Last name is required");
    };

   const emailRegexp = /^[\w.+'-]+@[\w.-]+\.(?!c0m$|cim$|con$|c0n$|usa$)[A-Za-z]{2,}$/i;

    const emailChangeHandler = (val: string) => {
        setEmail(val);

        if (!val) {
            setEmailError("Email is required");
        } else if (!emailRegexp.test(val)) {
            setEmailError("Enter a valid email");
        } else {
            setEmailError("");
        }
    };

    const handleBrandNameChange = (val: string) => {
        setBrandName(val);
        setBrandNameError(val.trim() ? "" : "Brand name is required");
    };

    const handleProductNumberChange = (val: string) => setProductNumber(val);
    const handleAdditionalInfoChange = (val: string) => setAdditionalInfo(val);

    const validateForm = () => {
        let valid = true;

        if (!firstName.trim()) {
            setFirstNameError("First name is required");
            valid = false;
        }

        if (!lastName.trim()) {
            setLastNameError("Last name is required");
            valid = false;
        }

        if (!email.trim()) {
            setEmailError("Email is required");
            valid = false;
        } else if (!emailRegexp.test(email)) {
            setEmailError("Enter a valid email");
            valid = false;
        }

        if (!brandName.trim()) {
            setBrandNameError("Brand name is required");
            valid = false;
        }

        return valid;
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);

        submitProductRequestFormHandler({
            firstName,
            lastName,
            email,
            brandName,
            productNumber,
            addionalProductInfo: additionalInfo,

            onSuccess: response => {
                setIsLoading(false);
                setOnSuccess(false)
            },
            onError: () => {
                setIsLoading(false);
            },
            onComplete: () => {},
        });
    };

    return (
        <>
            {onSuccess ? (
                isLoading ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner />
                    </StyledWrapper>
                ) : (
                    <StyledWrapper>
                        <StyledForm
                            id="availabilityForm"
                            noValidate={true}
                            onSubmit={handleFormSubmit}
                            data-test-selector="availabilityForm"
                        >
                            <StyledFieldSet {...styles.fieldset}>
                                <StyledWrapper
                                    style={{
                                        display: "flex",
                                        gap: "20px",
                                        width: "100%",
                                    }}
                                    {...styles.firstLastNameStyle}
                                >
                                    <TextField
                                        label="First Name"
                                        value={firstName}
                                        required
                                        placeholder="First Name"
                                        error={firstNameError}
                                        onChange={e => handleFirstNameChange(e.target.value)}
                                    />

                                    <TextField
                                        label="Last Name"
                                        value={lastName}
                                        required
                                        placeholder="Last Name"
                                        error={lastNameError}
                                        onChange={e => handleLastNameChange(e.target.value)}
                                    />
                                </StyledWrapper>

                                <StyledWrapper style={{ marginTop: "16px" }}>
                                    <TextField
                                        label="Email Address"
                                        value={email}
                                        required
                                        placeholder="Email"
                                        error={emailError}
                                        onChange={e => emailChangeHandler(e.target.value)}
                                    />
                                </StyledWrapper>

                                <StyledWrapper style={{ marginTop: "16px" }}>
                                    <TextField
                                        label="Enter Brand Name"
                                        value={brandName}
                                        required
                                        placeholder="Which brand are you looking for?"
                                        error={brandNameError}
                                        onChange={e => handleBrandNameChange(e.target.value)}
                                    />
                                </StyledWrapper>

                                <StyledWrapper style={{ marginTop: "16px" }}>
                                    <TextField
                                        label="Enter Product Number"
                                        value={productNumber}
                                        placeholder="Enter Product Number"
                                        onChange={e => handleProductNumberChange(e.target.value)}
                                    />
                                </StyledWrapper>

                                <StyledWrapper style={{ marginTop: "16px" }}>
                                    <TextArea
                                        label="Additional Product Information"
                                        placeholder="Provide any additional details here"
                                        value={additionalInfo}
                                        onChange={e => handleAdditionalInfoChange(e.target.value)}
                                    />
                                </StyledWrapper>

                                <Button {...styles.submitButton} type="submit">
                                    {translate("Submit")}
                                </Button>
                            </StyledFieldSet>
                        </StyledForm>
                    </StyledWrapper>
                )
            ) : (
                <Typography
                    style={{
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: "24px",
                        color: "#636161",
                        marginTop: "10px",
                    }}
                >
                    {translate("We appreciate your request. A member of Nisco Inc. will reach out to you shortly!")}
                </Typography>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(NISNoSearchOptimizelyForm),
    definition: {
        group: "Basic",
        displayName: "NIS No Search Optimizely Form",
    },
};

export default widgetModule;
